/**
 * Identificador único, con o sin contexto seguro.
 *
 * `crypto.randomUUID()` sólo existe en contextos seguros —HTTPS o localhost— y
 * en iOS recién desde la 15.4. Cargando un trabajo desde un teléfono, contra el
 * servidor de la red local por HTTP, no está: la pantalla mostraba
 * `crypto.randomUUID is not a function` y el trabajo no se podía guardar.
 *
 * `crypto.getRandomValues()`, en cambio, está en todos lados y no depende del
 * contexto seguro. Con eso se arma el UUID v4 a mano, que es lo que hace
 * `randomUUID` por dentro.
 *
 * Si no hubiera NINGUNA fuente criptográfica, esto falla en vez de recurrir a
 * `Math.random`. No es purismo: este identificador es la clave de idempotencia
 * del alta. Dos trabajos distintos que cayeran en el mismo identificador harían
 * que el segundo se descartara como repetido y **se perdiera sin aviso**. Ante
 * eso, negarse a guardar es mejor que guardar mal.
 */
export function uuidV4(): string {
  const c = globalThis.crypto;

  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error('Este navegador no puede generar identificadores seguros');
  }

  const b = new Uint8Array(16);
  c.getRandomValues(b);
  // Versión 4 y variante RFC 4122, igual que `randomUUID`.
  b[6] = ((b[6] as number) & 0x0f) | 0x40;
  b[8] = ((b[8] as number) & 0x3f) | 0x80;

  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return [
    h.slice(0, 4).join(''),
    h.slice(4, 6).join(''),
    h.slice(6, 8).join(''),
    h.slice(8, 10).join(''),
    h.slice(10, 16).join(''),
  ].join('-');
}
